import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { Gym, NetworkType } from '../model/gym';
import { StudioType } from '../model/studio-type';
import { GeocodingAddress } from '../model/geocoding-address';
import { GeocodingPrediction } from '../model/geocoding-prediction';
import { Slugs } from '../model/slugs';
import { GymImage } from '../model/gymImage';
import { Options } from '../model/options';
import { FeatureFlags } from '../model/feature-flags';

@Injectable({
  providedIn: 'root',
})
export class GymFinderService {
  private studioTypes: StudioType[]; // cached studio types

  constructor(private readonly httpClient: HttpClient) {}

  public exportGymsAsCsv(): Observable<Blob> {
    const url = `/upstream/gymfinder/internal/admin/v1/gym/export`;
    return this.httpClient.get(url, {
      withCredentials: true,
      responseType: 'blob',
    });
  }

  public getGym(gymId: number): Observable<Gym> {
    const url = `/upstream/gymfinder/internal/admin/v1/gym/${gymId}`;
    return this.httpClient.get<Gym>(url, { withCredentials: true });
  }

  public updateGym(gym: Gym): Observable<Gym> {
    const url = `/upstream/gymfinder/internal/admin/v1/gym/${gym.gymId}`;
    return this.httpClient.put<Gym>(url, gym, { withCredentials: true });
  }

  public importGym(gymId: number, slugs: Slugs): Observable<Gym> {
    const url = `/upstream/gymfinder/internal/admin/v1/gym/import/${gymId}`;
    return this.httpClient.post<Gym>(url, slugs, { withCredentials: true });
  }

  public getStudioTypes(): Observable<StudioType[]> {
    if (this.studioTypes && this.studioTypes.length > 0) {
      return of(this.studioTypes);
    } else {
      return this.httpClient
        .get<
          StudioType[]
        >('/upstream/gymfinder/internal/admin/v1/studio-types', { withCredentials: true })
        .pipe(
          tap(data => {
            this.studioTypes = data;
          })
        );
    }
  }

  public getOptions(network: NetworkType | null): Observable<Options> {
    return this.httpClient.get<Options>(
      `/upstream/gymfinder/internal/admin/v1/option${network ? `?network=${network}` : ''}`,
      { withCredentials: true }
    );
  }

  public validateAddress(address: string): Observable<GeocodingAddress> {
    let params = new HttpParams();
    params = params.append('location', address);
    const url = `/upstream/gymfinder/internal/admin/v1/geocoding/resolve`;
    return this.httpClient.get<GeocodingAddress>(url, {
      withCredentials: true,
      params: params,
    });
  }

  public autocompleteAddress(location: string): Observable<GeocodingPrediction[]> {
    let params = new HttpParams();
    params = params.append('location', location);
    const url = `/upstream/gymfinder/internal/admin/v1/geocoding/autocomplete`;
    return this.httpClient.get<GeocodingPrediction[]>(url, {
      withCredentials: true,
      params: params,
    });
  }

  public findPlaceById(placeId: string): Observable<GeocodingAddress> {
    const url = `/upstream/gymfinder/internal/admin/v1/geocoding/place/${placeId}`;
    return this.httpClient.get<GeocodingAddress>(url, { withCredentials: true });
  }

  public syncGym(gymId: number): Observable<Gym> {
    const url = `/upstream/gymfinder/internal/admin/v1/gym/${gymId}/sync`;
    return this.httpClient.post<Gym>(url, null, { withCredentials: true });
  }

  public addImages(gymId: number, images: File[]): Observable<GymImage[]> {
    const url = `/upstream/gymfinder/internal/admin/v2/gym/${gymId}/image`;
    const formData = new FormData();

    for (const image of images) {
      formData.append('images', image);
    }

    return this.httpClient.post<GymImage[]>(url, formData, {
      withCredentials: true,
    });
  }

  public deleteImages(gymId: number, imageIds: string[]): Observable<void> {
    const url = `/upstream/gymfinder/internal/admin/v1/gym/${gymId}/image`;
    return this.httpClient.delete<void>(url, {
      withCredentials: true,
      body: imageIds,
    });
  }

  public getFeatureFlags(): Observable<FeatureFlags> {
    const url = `/upstream/gymfinder/internal/admin/v1/feature-flags`;
    return this.httpClient.get<FeatureFlags>(url, {
      withCredentials: true,
    });
  }

  public getEmailSendingTransactions(): Observable<string[]> {
    const url = `/upstream/gymfinder/internal/admin/v1/email-sending-transactions`;
    return this.httpClient.get<string[]>(url, {
      withCredentials: true,
    });
  }
}
